import { Link, useParams } from 'react-router-dom';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { topics } from '@/mocks/topics';
import { topicDetails } from '@/mocks/topicDetails';
import { useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://hnbhcpa.cn';

export default function TopicDetailPage() {
  const { id } = useParams();
  const topic = topics.find((t) => t.id === id);
  const detail = topicDetails.find((d) => d.id === id);

  useSeo({
    title: topic ? `${topic.title} - 爆料专区话题 - 爆料专区导航` : '话题未找到 - 爆料专区导航',
    description: topic ? topic.summary : '该话题可能已被移动或下架，请返回热门话题页查看其他内容。',
    keywords: topic
      ? `${topic.categoryName},爆料专区,最新爆料帖,爆料专区话题,${topic.title}`
      : '爆料专区,热门话题',
    path: topic ? `/topics/${topic.id}` : '/topics',
    schema: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: topic.title,
            description: topic.summary,
            datePublished: topic.date,
            dateModified: topic.date,
            author: { '@type': 'Organization', name: topic.author },
            publisher: { '@type': 'Organization', name: '爆料专区导航' },
            articleSection: topic.categoryName,
            inLanguage: 'zh-CN',
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/topics/${topic.id}` },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: '热门话题', item: `${SITE_URL}/topics` },
              { '@type': 'ListItem', position: 3, name: topic.title, item: `${SITE_URL}/topics/${topic.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!topic || !detail) {
    return (
      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-8 lg:px-12 py-24 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center text-3xl">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="font-heading mt-5 text-2xl md:text-3xl font-bold text-foreground-950">话题未找到</h1>
          <p className="mt-3 text-sm text-foreground-600">该话题可能已被移动或下架，去热门话题页看看其他内容吧。</p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回热门话题
          </Link>
        </div>
      </main>
    );
  }

  const related = topics.filter((t) => t.id !== topic.id && t.category === topic.category).slice(0, 3);
  const fallbackRelated = topics.filter((t) => t.id !== topic.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  return (
    <main className="pt-16 md:pt-20">
      <article>
        <header className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
            <Breadcrumbs
              items={[
                { label: '首页', path: '/' },
                { label: '热门话题', path: '/topics' },
                { label: topic.categoryName, path: `/topics?category=${topic.category}` },
                { label: topic.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800">{topic.categoryName}</span>
              <span>{topic.date}</span>
              <span>· 阅读 {topic.reading}</span>
              <span className="inline-flex items-center gap-1 text-primary-600">
                <i className="ri-fire-line" />
                {topic.hot.toLocaleString()} 热度
              </span>
            </div>
            <h1 className="font-heading mt-4 max-w-4xl text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {topic.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">{topic.summary}</p>
            <p className="mt-4 text-xs text-foreground-500">整理：{topic.author}</p>
          </div>
        </header>

        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={topic.image}
                  alt={`${topic.title}配图`}
                  title={`${topic.title} · 爆料专区话题`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <p className="mt-8 text-base md:text-lg leading-loose text-foreground-800 border-l-2 border-primary-400 pl-5">
                {detail.lead}
              </p>

              {detail.sections.map((section) => (
                <section key={section.heading} className="mt-9">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 24)} className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}

              <section className="mt-10 bg-accent-50 border border-accent-200 rounded-lg p-6">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">要点小结</h2>
                <ul className="mt-4 space-y-3">
                  {detail.takeaways.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-accent-600 text-base" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="bg-secondary-950 rounded-lg p-6 text-secondary-100">
                  <h2 className="font-heading text-base font-semibold text-background-50">阅读提示</h2>
                  <p className="mt-3 text-sm leading-relaxed text-secondary-300">
                    本文属于线索整理，不代表最终结论。请结合官方渠道发布的信息自行判断，理性参与讨论。
                  </p>
                  <Link
                    to="/guide"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看信息判断技巧
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>

                <div className="bg-background-100 rounded-lg p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">相关话题推荐</h2>
                  <ul className="mt-4 space-y-4">
                    {relatedList.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/topics/${item.id}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <span className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                            <img
                              src={item.image}
                              alt={`${item.title}配图`}
                              title={`${item.title} · 爆料专区话题`}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">{item.date}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </main>
  );
}